/****************************************************************************
 * Responsibility - Handles reading and updating the for the user's account.
 * **************************************************************************
 *
 * @format */

import {
    SelectWithErrorHandler,
    InputWithErrorHandler,
    CommaSeparatedInput,
    Textarea
} from 'frontier-form';
import {CHANNEL_OTHER, CHANNELS, REASON_OTHER, REASONS} from 'frontier-constants';
import {PasswordValidator} from 'frontier/utilities/password_validator';
import UserTextSettings from '../../user_text_setting/user_text_setting';
import {OrganizationalRelationships} from './organizational_relationships';
import {Checkbox, Button} from '@sttark/design-system';
import {Toast} from '@sttark/design-system';
import {If} from 'frontier-utilities';
import React, {useState} from 'react';

export const Contact = props => {
    const [lastUpdatedField, setLastUpdatedField] = useState(null);

    const selectedChannel =
        CHANNELS.find(channel => channel.id === props.formData.channel_id)?.label || '';
    const selectedReason =
        REASONS.find(reason => reason.id === props.formData.reason_id)?.label || '';

    return (
        <>
            <Toast autoHide isOpen={!!props.formDataPassword.showToast} zIndex={3000}>
                <p className="m-0 color-structure-white">Password updated successfully!</p>
            </Toast>
            <section className="account-preferences-section-10mc bg-structure-lightest">
                <h3 className={'account-preferences-section-10mc__heading'}>Contact</h3>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group">
                        <InputWithErrorHandler
                            onChange={props.onInputChange.bind(this, 'formData.full_name')}
                            onKeyDown={() => setLastUpdatedField('full_name')}
                            className="form-control form-control-sm"
                            value={props.formData.full_name || ''}
                            label="* Primary contact full name"
                            error={props.accountDataError}
                            placeholder="* Enter your name"
                            autoComplete={'off'}
                            errorPath="name"
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                            fieldName="full_name"
                        />
                    </div>
                    <div className="form-group">
                        <InputWithErrorHandler
                            onChange={props.onInputChange.bind(this, 'formData.username')}
                            onKeyDown={() => setLastUpdatedField('username')}
                            label="* Primary contact email address (username)"
                            className="form-control form-control-sm"
                            value={props.formData.username || ''}
                            placeholder="* Enter your email"
                            error={props.accountDataError}
                            errorPath="username"
                            autoComplete={'off'}
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="username"
                            isLoading={props.savingData}
                        />
                    </div>
                </div>

                <div className="account-preferences-section-10mc__row">
                    <div className="form-group">
                        <InputWithErrorHandler
                            onChange={props.onPhoneInputChange.bind(this, 'formData.phone')}
                            onKeyDown={() => setLastUpdatedField('phone')}
                            className="form-control form-control-sm"
                            placeholder="* Enter your phone number"
                            label="* Primary contact phone number"
                            value={props.formData.phone || ''}
                            error={props.accountDataError}
                            errorPath="phone"
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="phone"
                            isLoading={props.savingData}
                        />
                    </div>
                    <div className="form-group">
                        <InputWithErrorHandler
                            onChange={props.onInputChange.bind(this, 'formData.company')}
                            onKeyDown={() => setLastUpdatedField('company')}
                            className="form-control form-control-sm"
                            value={props.formData.company || ''}
                            placeholder="* Enter Company name"
                            errorPath="company_entity.name"
                            error={props.accountDataError}
                            label="* Company name"
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="company"
                            isLoading={props.savingData}
                        />
                    </div>
                </div>

                <hr />

                <div className="account-preferences-section-10mc__row align-items-start">
                    <div className="form-group">
                        <SelectWithErrorHandler
                            error={
                                !props.formData.channel_id
                                    ? 'Help us serve more great customers like you!'
                                    : false
                            }
                            onChange={props.onInputChange.bind(this, 'formData.channel_id')}
                            className="form-control form-control-sm"
                            placeholder="How did you find us?"
                            initialValue={selectedChannel}
                            label="How did you find us?"
                            options={CHANNELS}
                            emptyText=" "
                            newestds
                        />

                        {parseInt(props.formData.channel_id) === CHANNEL_OTHER && (
                            <div className="d-flex align-items-center justify-content-start gap-3 pt-4">
                                <label>
                                    <b>Other</b>
                                </label>
                                {(!props.formData.channel_other ||
                                    props.formData.channel_other === '') && (
                                    <span className="ml-2 text-danger">
                                        Please provide your reason.
                                    </span>
                                )}
                                <Textarea
                                    onClick={() => setLastUpdatedField('channel_id')}
                                    value={props.formData.channel_other || ''}
                                    onChange={props.onInputChange.bind(
                                        this,
                                        'formData.channel_other'
                                    )}
                                    className="form-control"
                                />
                            </div>
                        )}

                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                            fieldName="channel_id"
                        />
                    </div>

                    <div className="form-group">
                        <SelectWithErrorHandler
                            onChange={props.onInputChange.bind(this, 'formData.reason_id')}
                            error={
                                !props.formData.reason_id
                                    ? 'Help us serve more great customers like you!'
                                    : false
                            }
                            placeholder="What is most important to you?"
                            className="form-control form-control-sm"
                            label="What is most important to you?"
                            initialValue={selectedReason}
                            options={REASONS}
                            emptyText=" "
                            newestds
                        />
                        {parseInt(props.formData.reason_id) === REASON_OTHER && (
                            <div className="mt-4">
                                <label>
                                    <b>Other</b>
                                </label>
                                {(!props.formData.reason_other ||
                                    props.formData.reason_other === '') && (
                                    <span className="ml-2 text-danger">
                                        Please provide your reason.
                                    </span>
                                )}
                                <Textarea
                                    onClick={() => setLastUpdatedField('reason_id')}
                                    onKeyDown={() => setLastUpdatedField('reason_id')}
                                    value={props.formData.reason_other || ''}
                                    onChange={props.onInputChange.bind(
                                        this,
                                        'formData.reason_other'
                                    )}
                                    className="form-control"
                                />
                            </div>
                        )}

                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                            fieldName="reason_id"
                        />
                    </div>
                </div>

                <hr />

                <h3 className="account-preferences-section-10mc__heading">Notification Methods</h3>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <CommaSeparatedInput
                            onChange={props.onInputChange.bind(this, 'formData.proof_email')}
                            onKeyDown={() => setLastUpdatedField('proof_email')}
                            placeholder="Enter proofing email address(es)"
                            value={props.formData.proof_email || ''}
                            error={props.accountDataError}
                            className="form-control w-100"
                            errorPath="proof_email"
                            label="Proofing email"
                            separator=" "
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            isLoading={props.savingData}
                            fieldName="proof_email"
                        />
                    </div>
                </div>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <CommaSeparatedInput
                            onChange={props.onInputChange.bind(this, 'formData.billing_email')}
                            onKeyDown={() => setLastUpdatedField('billing_email')}
                            placeholder="Enter billing email address(es)"
                            value={props.formData.billing_email || ''}
                            error={props.accountDataError}
                            className="form-control w-100"
                            errorPath="billing_email"
                            label="Billing email"
                            separator=" "
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="billing_email"
                            isLoading={props.savingData}
                        />
                    </div>
                </div>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group w-100">
                        <CommaSeparatedInput
                            onChange={props.onInputChange.bind(this, 'formData.shipping_email')}
                            onKeyDown={() => setLastUpdatedField('shipping_email')}
                            placeholder="Enter shipping email address(es)"
                            value={props.formData.shipping_email || ''}
                            error={props.accountDataError}
                            className="form-control w-100"
                            errorPath="shipping_email"
                            label="Shipping email"
                            separator=" "
                            newestds
                        />
                        <SavingChangesIndicator
                            lastUpdatedField={lastUpdatedField}
                            fieldName="shipping_email"
                            isLoading={props.savingData}
                        />
                    </div>
                </div>

                <UserTextSettings id={props.user.id} />

                <OrganizationalRelationships />

                <hr />

                <h3 className="account-preferences-section-10mc__heading">Shipment Settings</h3>
                <div className="account-preferences-section-10mc__row justify-content-start">
                    <div className="account-preferences-section-10mc__checkbox">
                        <p className="m-0">
                            <b>Packing list</b>
                        </p>
                        <Checkbox
                            onChange={props.onCheckboxChange.bind(
                                this,
                                'formData.is_paper_packing_list'
                            )}
                            onKeyDown={() => setLastUpdatedField('is_paper_packing_list')}
                            label="Always include paper packing list with shipments"
                            checked={props.formData.is_paper_packing_list || false}
                            bold={props.formData.is_paper_packing_list || false}
                            value={props.formData.is_paper_packing_list || ''}
                            error={props.error}
                        />
                    </div>
                    <div className="account-preferences-section-10mc__checkbox">
                        <p className="m-0">
                            <b>Ship blind</b>
                        </p>
                        <Checkbox
                            onKeyDown={() => setLastUpdatedField('is_default_blind_shipping')}
                            checked={props.formData.is_default_blind_shipping || false}
                            bold={props.formData.is_default_blind_shipping || false}
                            value={props.formData.is_default_blind_shipping || ''}
                            label="Remove Sttark branding from roll cores"
                            onChange={props.onCheckboxChange.bind(
                                this,
                                'formData.is_default_blind_shipping'
                            )}
                            error={props.error}
                        />
                    </div>
                </div>

                <hr />

                <h3 className="account-preferences-section-10mc__heading">Password</h3>
                <div className="account-preferences-section-10mc__row">
                    <div className="form-group">
                        <InputWithErrorHandler
                            onChange={props.onInputChange.bind(
                                this,
                                'formDataPassword.currentPassword'
                            )}
                            value={props.formDataPassword.currentPassword || ''}
                            className="form-control form-control-sm"
                            placeholder="Enter current password"
                            errorPath={'current_password'}
                            error={props.passwordError}
                            label="Current password"
                            type="password"
                            newestds
                        />
                    </div>
                    <div className="form-group">
                        <InputWithErrorHandler
                            value={props.formDataPassword.newPassword || ''}
                            onChange={props.onInputChange.bind(
                                this,
                                'formDataPassword.newPassword'
                            )}
                            className="form-control form-control-sm"
                            placeholder="Enter desired new password"
                            error={props.passwordError}
                            errorPath={'new_password'}
                            label="New password"
                            type="password"
                            newestds
                        />
                        <If condition={!!props.formDataPassword.newPassword}>
                            <PasswordValidator
                                handleIsValidPassword={val =>
                                    props.handleCheckPasswordValidation(val)
                                }
                                input={props.formDataPassword.newPassword || ''}
                            />
                        </If>
                    </div>

                    <Button
                        disabled={props.savingPassword || !props.formDataPassword.isPasswordValid}
                        label={!props.savingPassword ? 'Update Password' : 'Saving...'}
                        onClick={props.onSavePassword}
                        primary
                    />
                </div>
            </section>
        </>
    );
};

export const SavingChangesIndicator = props => {
    const {isLoading, fieldName, lastUpdatedField} = props;
    return (
        <If condition={isLoading && fieldName === lastUpdatedField}>
            <i className="fa fa-spinner fa-spin color-accent-primary d-inline-block me-2" />
            <span className="color-accent-primary">Saving changes</span>
        </If>
    );
};
